"use client";

import { usePathname } from "next/navigation";
import { useContext, useEffect, useRef } from "react";
import { screenSizeContext } from "@/app/store/context/screenSizesContext";
import { loadingContext } from "@/app/store/context/isLoadingContext";
import type Lenis from "lenis";

interface ScrollSmootherProps {
	children: React.ReactNode;
}

declare global {
	interface Window {
		lenis:
			| (Lenis & {
					stopScroll?: (force?: boolean) => void;
					startScroll?: (force?: boolean) => void;
			  })
			| undefined;
	}
}

/**
 * Desktop-only Lenis + GSAP bridge. Heavy libs are dynamically imported after
 * viewport is known so mobile does not download/parse them on first load.
 */
const ScrollSmootherContainer: React.FC<ScrollSmootherProps> = ({
	children,
}) => {
	const { setIsPageFetched } = useContext(loadingContext);
	const { isIpad, isReady } = useContext(screenSizeContext);
	const pathname = usePathname();

	const lenisRef = useRef<Lenis | null>(null);
	const forceStop = useRef<boolean>(true);

	useEffect(() => {
		if (!isReady) return;

		if (isIpad) {
			window.history.scrollRestoration = "manual";
			document.documentElement.style.overflow = "hidden";
			return () => {
				document.documentElement.style.overflow = "";
			};
		}

		let cancelled = false;
		let resizeTimer: ReturnType<typeof setTimeout> | undefined;
		let contentObserver: ResizeObserver | undefined;
		let update: ((time: number) => void) | undefined;
		let syncScrollMetrics: (() => void) | undefined;
		let gsapTicker: { add: (fn: (t: number) => void) => void; remove: (fn: (t: number) => void) => void } | undefined;
		let scrollTriggerUpdate: ((...args: unknown[]) => void) | undefined;

		void (async () => {
			const [{ default: LenisCtor }, { default: gsap }, { ScrollTrigger }, { default: ScrollToPlugin }, { SplitText }] =
				await Promise.all([
					import("lenis"),
					import("gsap"),
					import("gsap/ScrollTrigger"),
					import("gsap/ScrollToPlugin"),
					import("gsap/SplitText"),
				]);
			await import("lenis/dist/lenis.css");

			if (cancelled) return;

			gsap.config({ nullTargetWarn: false, force3D: true });
			gsap.registerPlugin(ScrollTrigger, ScrollToPlugin, SplitText);
			gsapTicker = gsap.ticker;
			scrollTriggerUpdate = ScrollTrigger.update as (...args: unknown[]) => void;

			lenisRef.current = new LenisCtor({
				duration: 1.5,
				syncTouch: true,
				smoothWheel: true,
				wheelMultiplier: 1,
				autoResize: false,
			});

			update = (time: number) => {
				lenisRef.current?.raf(time * 1000);
			};

			syncScrollMetrics = () => {
				clearTimeout(resizeTimer);
				resizeTimer = setTimeout(() => {
					lenisRef.current?.resize();
					ScrollTrigger.refresh();
				}, 150);
			};

			lenisRef.current.on("scroll", ScrollTrigger.update);
			gsap.ticker.add(update);
			window.addEventListener("resize", syncScrollMetrics);
			contentObserver = new ResizeObserver(syncScrollMetrics);
			contentObserver.observe(document.body);

			lenisRef.current.stop();

			window.lenis = lenisRef.current;
			window.lenis.stopScroll = (force?: boolean) => {
				if (force) forceStop.current = true;
				lenisRef.current?.stop();
			};
			window.lenis.startScroll = (force?: boolean) => {
				if (!forceStop.current) {
					lenisRef.current?.start();
				} else if (force) {
					lenisRef.current?.start();
					forceStop.current = false;
				}
			};
		})().catch((err) => {
			console.error("[ScrollSmoother] failed to init Lenis/GSAP", err);
		});

		return () => {
			cancelled = true;
			clearTimeout(resizeTimer);
			if (syncScrollMetrics) {
				window.removeEventListener("resize", syncScrollMetrics);
			}
			contentObserver?.disconnect();
			if (update && gsapTicker) gsapTicker.remove(update);
			if (scrollTriggerUpdate && lenisRef.current) {
				lenisRef.current.off("scroll", scrollTriggerUpdate);
			}
			lenisRef.current?.destroy();
			lenisRef.current = null;
			window.lenis = undefined;
		};
	}, [isIpad, isReady]);

	const isInitialPath = useRef(true);

	useEffect(() => {
		if (isInitialPath.current) {
			isInitialPath.current = false;
			return;
		}
		setIsPageFetched("loading");
	}, [pathname, setIsPageFetched]);

	return <>{children}</>;
};

export default ScrollSmootherContainer;
